import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiCalendar,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { calendarAPI } from "../../services/api";
import AdminLayout from "../../components/layout/AdminLayout";
import Modal from "../../components/common/Modal";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { toNepaliDigits } from "../../utils/nepaliCalendar";

const emptyForm = { bsDay: "", title: "", description: "", isHoliday: false };

const AdminCalendar = () => {
  const [year, setYear] = useState(null);
  const [month, setMonth] = useState(null);
  const [monthData, setMonthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    calendarAPI
      .getToday()
      .then((res) => {
        const t = res.data?.data;
        setYear(t.bsYear);
        setMonth(t.bsMonth);
      })
      .catch(() => toast.error("Failed to load calendar"));
  }, []);

  const load = useCallback(() => {
    if (!year || !month) return;
    setLoading(true);
    calendarAPI
      .getAdminMonth({ year, month })
      .then((res) => setMonthData(res.data?.data))
      .catch(() => toast.error("Failed to load events"))
      .finally(() => setLoading(false));
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  const goPrev = () => {
    if (month === 1) {
      setMonth(12);
      setYear((y) => y - 1);
    } else {
      setMonth((m) => m - 1);
    }
  };

  const goNext = () => {
    if (month === 12) {
      setMonth(1);
      setYear((y) => y + 1);
    } else {
      setMonth((m) => m + 1);
    }
  };

  const openCreate = () => {
    setEditItem(null);
    setForm(emptyForm);
    setModal(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      bsDay: item.bsDay,
      title: item.title,
      description: item.description || "",
      isHoliday: item.isHoliday,
    });
    setModal(true);
  };

  const handleSave = async () => {
    const day = parseInt(form.bsDay, 10);
    if (!form.title.trim() || !day) {
      toast.error("Day and title are required");
      return;
    }
    if (monthData && (day < 1 || day > monthData.daysInMonth)) {
      toast.error(`Day must be between 1 and ${monthData.daysInMonth}`);
      return;
    }
    setSaving(true);
    try {
      if (editItem) {
        await calendarAPI.update(editItem._id, {
          bsDay: day,
          title: form.title.trim(),
          description: form.description.trim(),
          isHoliday: form.isHoliday,
        });
        toast.success("Event updated");
      } else {
        await calendarAPI.create({
          bsYear: year,
          bsMonth: month,
          bsDay: day,
          title: form.title.trim(),
          description: form.description.trim(),
          isHoliday: form.isHoliday,
        });
        toast.success("Event created");
      }
      setModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save event");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await calendarAPI.delete(id);
      toast.success("Event deleted");
      setDeleteConfirm(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete event");
    }
  };

  const events = monthData?.events || [];

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-serif font-bold text-primary-500">
            Nepali Calendar
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Manage BS calendar events and holidays. Saturdays &amp; Sundays
            are holidays by default.
          </p>
        </div>
        <button
          onClick={openCreate}
          disabled={!monthData}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600 transition-colors text-sm disabled:opacity-50"
        >
          <FiPlus className="w-4 h-4" /> New Event
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-6 flex items-center justify-between">
        <button
          onClick={goPrev}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
        >
          <FiChevronLeft className="w-4 h-4" />
        </button>
        <h3 className="font-semibold text-primary-600">
          {monthData ? `${monthData.monthNameEn} ${year}` : "Loading..."}
        </h3>
        <button
          onClick={goNext}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
        >
          <FiChevronRight className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-5 py-3.5 text-gray-500 font-semibold">
                    Day
                  </th>
                  <th className="text-left px-5 py-3.5 text-gray-500 font-semibold">
                    Title
                  </th>
                  <th className="text-left px-5 py-3.5 text-gray-500 font-semibold hidden md:table-cell">
                    Description
                  </th>
                  <th className="text-left px-5 py-3.5 text-gray-500 font-semibold">
                    Type
                  </th>
                  <th className="text-left px-5 py-3.5 text-gray-500 font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {events.map((e, idx) => (
                  <motion.tr
                    key={e._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.03 }}
                    className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-5 py-4 font-medium text-gray-800">
                      {toNepaliDigits(e.bsDay)}
                    </td>
                    <td className="px-5 py-4 text-gray-700">{e.title}</td>
                    <td className="px-5 py-4 text-gray-500 text-xs hidden md:table-cell max-w-xs truncate">
                      {e.description || "-"}
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          e.isHoliday
                            ? "bg-red-100 text-red-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {e.isHoliday ? "Holiday" : "Event"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => openEdit(e)}
                          className="p-1.5 text-primary-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <FiEdit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(e._id)}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <FiTrash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
          {events.length === 0 && (
            <div className="text-center py-12">
              <FiCalendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No events for this month</p>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modal}
        onClose={() => setModal(false)}
        title={
          editItem
            ? "Edit Event"
            : `New Event — ${monthData?.monthNameEn || ""} ${year || ""}`
        }
        footer={
          <>
            <button
              onClick={() => setModal(false)}
              className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-xl text-sm font-medium hover:bg-primary-600 disabled:opacity-70"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : null}
              {editItem ? "Update" : "Create"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Day *
            </label>
            <select
              value={form.bsDay}
              onChange={(e) =>
                setForm((p) => ({ ...p, bsDay: e.target.value }))
              }
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none bg-white"
            >
              <option value="">Select day</option>
              {Array.from(
                { length: monthData?.daysInMonth || 32 },
                (_, i) => i + 1,
              ).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              value={form.title}
              onChange={(e) =>
                setForm((p) => ({ ...p, title: e.target.value }))
              }
              placeholder="Event title"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm((p) => ({ ...p, description: e.target.value }))
              }
              rows={3}
              placeholder="Optional details..."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none resize-none"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.isHoliday}
              onChange={(e) =>
                setForm((p) => ({ ...p, isHoliday: e.target.checked }))
              }
              className="w-4 h-4 rounded accent-primary-500"
            />
            <span className="text-sm font-medium text-gray-700">
              Mark as holiday (school closed)
            </span>
          </label>
        </div>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        size="sm"
        footer={
          <>
            <button
              onClick={() => setDeleteConfirm(null)}
              className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => handleDelete(deleteConfirm)}
              className="px-4 py-2 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600"
            >
              Delete
            </button>
          </>
        }
      >
        <p className="text-gray-600 text-sm">
          Are you sure you want to delete this event? This action cannot be
          undone.
        </p>
      </Modal>
    </AdminLayout>
  );
};

export default AdminCalendar;
